/**
 * AI 제공자 통합 관리 페이지
 *
 * - 탭: AI 제공자 | 임베딩 AI 제공자
 * - 리스트: DB에 어카운트/키가 있는 팀만 표시
 * - "+ 등록하기": 미설정 팀 선택 → 제공자 카드 → 연결하기
 */

import { useState, useEffect, useCallback } from 'react';
import { Topbar, PanelTitle } from '../components/layout';
import { Badge, Button, Modal, Select, Tabs, useToast } from '../components/ui';
import type { TabItem } from '../components/ui';
import { adminApi, BedrockAccount } from '../api/aiProvider';
import { embeddingProviderApi, EmbeddingAccount } from '../api/embeddingProvider';
import { authApi, tenantApi, customerApi, Tenant, Customer } from '../api';
import { teamApi, Team } from '../api/teams';
import { isProductAdminRole, isTenantAdminRole, isCustomerAdminRole, normalizeRole } from '../utils/role';
import { BedrockAccountModal } from '../components/chat/BedrockAccountModal';
import { EmbeddingAccountModal } from '../components/embedding/EmbeddingAccountModal';
import awsLogo from '../assets/icons/providers/aws.svg';
import openaiLogo from '../assets/icons/providers/openai.svg';
import anthropicLogo from '../assets/icons/providers/anthropic.svg';
import geminiLogo from '../assets/icons/providers/gemini.svg';
import EditIcon from '../assets/icons/common/edit.svg?react';
import '../styles/adminPage.css';

const AI_PROVIDERS = [
  { id: 'bedrock', name: 'Amazon Bedrock', logo: awsLogo, description: 'AWS의 기반 모델 서비스', models: [] },
  { id: 'openai', name: 'OpenAI', logo: openaiLogo, description: 'OpenAI 모델', models: [] },
  { id: 'anthropic', name: 'Anthropic', logo: anthropicLogo, description: 'Claude 모델', models: [] },
  { id: 'google', name: 'Google AI', logo: geminiLogo, description: 'Google 모델', models: [] },
];
const EMBEDDING_PROVIDERS = [
  { id: 'bedrock', name: 'Amazon Bedrock', logo: awsLogo, description: 'AWS 임베딩', models: [] },
  { id: 'openai', name: 'OpenAI', logo: openaiLogo, description: 'OpenAI 임베딩', models: [] },
];

type ProviderTab = 'ai' | 'embedding';

interface TeamData {
  team: Team;
  bedrockAccounts: BedrockAccount[];
  aiStatuses: Record<string, { connected: boolean; apiKeyMasked?: string }>;
  embedAccounts: EmbeddingAccount[];
  embedStatuses: Record<string, { connected: boolean; apiKeyMasked?: string }>;
  loaded: boolean;
}

export function ProviderConnectionPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const currentUser = authApi.getUser();
  const role = normalizeRole(currentUser?.role);
  const isProductAdmin = isProductAdminRole(role);
  const isTenantAdmin = isTenantAdminRole(role);
  const isCustomerAdmin = isCustomerAdminRole(role);
  const isTeamAdmin = role === 'teamAdmin';

  const [activeTab, setActiveTab] = useState<ProviderTab>('ai');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [teamDataMap, setTeamDataMap] = useState<Record<string, TeamData>>({});
  const [tenantFilter, setTenantFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');

  const [settingTeam, setSettingTeam] = useState<Team | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [regTenantId, setRegTenantId] = useState('');
  const [regCustomerId, setRegCustomerId] = useState('');
  const [regTeamId, setRegTeamId] = useState('');
  const [regCustomers, setRegCustomers] = useState<Customer[]>([]);
  const [regTeams, setRegTeams] = useState<Team[]>([]);
  const [regSelectedTeam, setRegSelectedTeam] = useState<Team | null>(null);

  const [testingId, setTestingId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [selectedBedrockTeamId, setSelectedBedrockTeamId] = useState<string | null>(null);
  const [selectedEmbedTeamId, setSelectedEmbedTeamId] = useState<string | null>(null);
  const [configProvider, setConfigProvider] = useState<{ teamId: string; providerId: string; isEmbed: boolean } | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKeyTested, setApiKeyTested] = useState(false);
  const [apiKeyTesting, setApiKeyTesting] = useState(false);
  // 삭제 확인 모달
  const [deleteConfirm, setDeleteConfirm] = useState<{ teamId: string; acc: any; isEmbed: boolean } | null>(null);

  // ── 초기 로드 ─────────────────────────────────────────────────────────────
  useEffect(() => { loadOrgs(); }, []);
  useEffect(() => { if (customers.length > 0 || isCustomerAdmin || isTeamAdmin) loadTeams(); }, [tenantFilter, customerFilter, customers]);

  const loadOrgs = async () => {
    try {
      const [ts, cs] = await Promise.all([
        isProductAdmin ? tenantApi.list() : Promise.resolve([]),
        customerApi.list(),
      ]);
      setTenants(ts);
      setCustomers(cs);
      // teamAdmin/customerAdmin은 customers 없이도 팀 로드
      if (isTeamAdmin || isCustomerAdmin) loadTeams();
    } catch { /**/ }
  };

  const loadTeams = useCallback(async () => {
    try {
      let newTeams: Team[] = [];
      if (isTeamAdmin && currentUser?.teamId) {
        const all = await teamApi.listByCustomer(currentUser.customerId || '').catch(() => []);
        const myTeam = all.find(t => t.id === currentUser.teamId);
        if (myTeam) newTeams = [myTeam];
      } else if (isCustomerAdmin && currentUser?.customerId) {
        newTeams = await teamApi.listByCustomer(currentUser.customerId);
      } else {
        const filtered = customers.filter(c =>
          (!tenantFilter || c.tenantId === tenantFilter) &&
          (!customerFilter || c.id === customerFilter)
        );
        const arrays = await Promise.all(filtered.map(c => teamApi.listByCustomer(c.id).catch(() => [])));
        newTeams = arrays.flat();
      }
      setAllTeams(newTeams);
      // 모든 팀 데이터 로드 (DB 기반으로 연결 여부 판단)
      newTeams.forEach(t => loadTeamData(t));
    } catch { /**/ }
  }, [tenantFilter, customerFilter, customers, isTeamAdmin, isCustomerAdmin, currentUser]);

  // ── 팀 데이터 로드 ────────────────────────────────────────────────────────
  const loadTeamData = useCallback(async (team: Team) => {
    const scope = `team:${team.id}`;
    const [aiAccounts, aiStatuses, embedAccounts, embedStatuses] = await Promise.all([
      adminApi.listBedrockAccounts(scope).catch(() => []),
      adminApi.getAllProviderStatuses(scope).catch(() => ({})),
      embeddingProviderApi.listAccounts(scope).catch(() => []),
      embeddingProviderApi.getAllProviderStatuses(scope).catch(() => ({})),
    ]);
    const data: TeamData = { team, bedrockAccounts: aiAccounts, aiStatuses, embedAccounts, embedStatuses, loaded: true };
    setTeamDataMap(prev => ({ ...prev, [team.id]: data }));
    return data;
  }, []);

  const refreshTeam = async (teamId: string) => {
    const team = allTeams.find(t => t.id === teamId) || settingTeam || regSelectedTeam;
    if (team) await loadTeamData(team);
  };

  const openSettingModal = async (team: Team) => {
    setSettingTeam(team);
    if (!teamDataMap[team.id]?.loaded) await loadTeamData(team);
  };

  // ── 등록하기 모달 ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isTeamAdmin && currentUser?.customerId) {
      // 팀 관리자: 본인 고객사/팀 고정
      setRegCustomers(customers.filter(c => c.id === currentUser.customerId));
      setRegCustomerId(currentUser.customerId);
    } else if (isCustomerAdmin && currentUser?.customerId) {
      setRegCustomers(customers.filter(c => c.id === currentUser.customerId));
      setRegCustomerId(currentUser.customerId);
    } else if (isTenantAdmin) {
      setRegCustomers(customers.filter(c => c.tenantId === currentUser?.tenantId));
    } else {
      setRegCustomers(customers);
    }
  }, [customers]);

  useEffect(() => {
    if (regCustomerId) {
      teamApi.listByCustomer(regCustomerId).then(teams => {
        setRegTeams(teams);
        // 팀 관리자는 자기 팀 자동 선택
        if (isTeamAdmin && currentUser?.teamId) {
          setRegTeamId(currentUser.teamId);
        }
      }).catch(() => setRegTeams([]));
      if (!isTeamAdmin) { setRegTeamId(''); setRegSelectedTeam(null); }
    }
  }, [regCustomerId]);

  useEffect(() => {
    if (regTeamId) {
      const team = regTeams.find(t => t.id === regTeamId) || null;
      setRegSelectedTeam(team);
      if (team) loadTeamData(team);
    } else { setRegSelectedTeam(null); }
  }, [regTeamId, regTeams]);

  // ── 이벤트 핸들러 ─────────────────────────────────────────────────────────
  const handleTestBedrockAccount = async (acc: BedrockAccount) => {
    setTestingId(acc.id);
    const tid = showToast('loading', '연결 테스트 중...', { title: acc.name });
    try { const r = await adminApi.testBedrockAccount(acc.id); showToast('success', r.message || '연결이 정상입니다.', { id: tid }); }
    catch (err: any) { showToast('error', '', { id: tid, title: '연결 실패', detail: err?.response?.data?.error || '테스트 실패' }); }
    finally { setTestingId(null); }
  };

  const handleDeleteBedrockAccount = async (teamId: string, acc: BedrockAccount) => {
    setDeleteConfirm({ teamId, acc, isEmbed: false });
  };

  const handleActivateBedrockAccount = async (teamId: string, acc: BedrockAccount) => {
    if (acc.isActive) return;
    setActivatingId(acc.id);
    try { await adminApi.activateBedrockAccount(acc.id); await refreshTeam(teamId); showToast('success', `"${acc.name}" 활성화되었습니다.`); }
    catch { showToast('error', '활성화에 실패했습니다.'); }
    finally { setActivatingId(null); }
  };

  const handleTestEmbedAccount = async (acc: EmbeddingAccount) => {
    setTestingId(acc.id);
    const tid = showToast('loading', '연결 테스트 중...', { title: acc.name });
    try { const r = await embeddingProviderApi.testAccount(acc.id); showToast('success', r.message || '연결이 정상입니다.', { id: tid }); }
    catch (err: any) { showToast('error', '', { id: tid, title: '연결 실패', detail: err?.response?.data?.error || '테스트 실패' }); }
    finally { setTestingId(null); }
  };

  const handleDeleteEmbedAccount = async (teamId: string, acc: EmbeddingAccount) => {
    setDeleteConfirm({ teamId, acc, isEmbed: true });
  };

  const executeDelete = async () => {
    if (!deleteConfirm) return;
    const { teamId, acc, isEmbed } = deleteConfirm;
    setDeleteConfirm(null);
    console.log('[executeDelete]', { accountId: acc.id, scope: acc.scope, teamId, isEmbed });
    try {
      if (isEmbed) await embeddingProviderApi.deleteAccount(acc.id, acc.scope || `team:${teamId}`);
      else await adminApi.deleteBedrockAccount(acc.id, acc.scope);
      await refreshTeam(teamId);
      showToast('success', '삭제되었습니다.');
    } catch (err: any) {
      console.error('[executeDelete] 삭제 실패:', err?.response?.data || err);
      showToast('error', err?.response?.data?.error || '삭제에 실패했습니다.');
    }
  };

  const handleActivateEmbedAccount = async (teamId: string, acc: EmbeddingAccount) => {
    if (acc.isActive) return;
    try { await embeddingProviderApi.activateAccount(acc.id); await refreshTeam(teamId); showToast('success', '활성화되었습니다.'); }
    catch { showToast('error', '활성화에 실패했습니다.'); }
  };

  const handleDisconnect = async (teamId: string, providerId: string, isEmbed: boolean) => {
    const scope = `team:${teamId}`;
    try {
      if (isEmbed) await embeddingProviderApi.deleteApiKey(providerId);
      else await adminApi.deleteApiKey(providerId, scope);
      await refreshTeam(teamId); showToast('success', '연결이 해제되었습니다.');
    } catch { showToast('error', '연결 해제에 실패했습니다.'); }
  };

  const handleTestApiKey = async () => {
    if (!configProvider || !apiKeyInput.trim()) return;
    const scope = `team:${configProvider.teamId}`;
    setApiKeyTesting(true);
    try {
      // 키 임시 저장 후 테스트
      if (configProvider.isEmbed) await embeddingProviderApi.saveApiKey(configProvider.providerId, apiKeyInput.trim(), scope);
      else await adminApi.saveApiKey(configProvider.providerId, apiKeyInput.trim(), scope);
      try {
        if (configProvider.isEmbed) await embeddingProviderApi.testConnection(configProvider.providerId);
        else await adminApi.testConnection(configProvider.providerId, scope);
        setApiKeyTested(true);
        showToast('success', '연결이 정상입니다.');
      } catch (testErr: any) {
        // 테스트 실패 → 저장된 키 롤백
        try {
          if (configProvider.isEmbed) await embeddingProviderApi.deleteApiKey(configProvider.providerId);
          else await adminApi.deleteApiKey(configProvider.providerId, scope);
        } catch { /* 롤백 실패 무시 */ }
        setApiKeyTested(false);
        showToast('error', testErr?.response?.data?.error?.message || 'API Key가 유효하지 않습니다. 다시 확인해주세요.');
      }
    } catch { showToast('error', '저장에 실패했습니다.'); }
    finally { setApiKeyTesting(false); }
  };

  const handleSaveApiKey = async () => {
    if (!configProvider || !apiKeyTested) return;
    await refreshTeam(configProvider.teamId);
    showToast('success', '연결이 완료되었습니다.');
    setConfigProvider(null); setApiKeyInput(''); setApiKeyTested(false);
  };

  // ── 연결된 제공자 이름 목록 ───────────────────────────────────────────────
  const getTeamConnectedProviders = (teamId: string, tab: ProviderTab) => {
    const d = teamDataMap[teamId];
    if (!d?.loaded) return null; // null = 로딩 중
    const providers = tab === 'ai' ? AI_PROVIDERS : EMBEDDING_PROVIDERS;
    return providers.filter(p => {
      if (p.id === 'bedrock') return tab === 'ai' ? d.bedrockAccounts.length > 0 : d.embedAccounts.length > 0;
      const s = (tab === 'ai' ? d.aiStatuses : d.embedStatuses)?.[p.id];
      return s?.connected;
    }).map(p => p.name);
  };

  // ── 팀이 "등록됨" 여부 (탭별 구분) ─────────────────────────────────────
  const isTeamConfiguredForTab = (teamId: string, tab: ProviderTab) => {
    const d = teamDataMap[teamId];
    if (!d?.loaded) return false;
    if (tab === 'ai') {
      return d.bedrockAccounts.length > 0 || Object.values(d.aiStatuses).some(s => s.connected);
    } else {
      return d.embedAccounts.length > 0 || Object.values(d.embedStatuses).some(s => s.connected);
    }
  };

  // ── 제공자 카드 렌더 ──────────────────────────────────────────────────────
  const renderProviderCard = (provider: typeof AI_PROVIDERS[0], teamId: string, isEmbed: boolean, d: TeamData) => {
    if (provider.id === 'bedrock') {
      const accounts = isEmbed ? d.embedAccounts : d.bedrockAccounts;
      const connected = accounts.length > 0;
      return (
        <div key={provider.id} className={`provider-card${connected ? ' connected' : ''}`}>
          <div className="provider-card-top">
            <div className="provider-logo"><img src={provider.logo} alt="" /></div>
            {connected ? <Badge color="success">연결됨</Badge> : <Badge color="gray">미연결</Badge>}
          </div>
          <div className="provider-name">{provider.name}</div>
          {connected ? (
            <>
              <div className="bedrock-account-list">
                {(accounts as any[]).map((acc: any) => (
                  <div key={acc.id} className={`bedrock-account-row${acc.isActive ? ' active' : ''}`}>
                    <label className="bedrock-account-radio">
                      <input type="radio" name={`b-${teamId}`} checked={!!acc.isActive}
                        onChange={() => isEmbed ? handleActivateEmbedAccount(teamId, acc) : handleActivateBedrockAccount(teamId, acc)}
                        disabled={activatingId !== null} />
                      <span className="bedrock-account-info">
                        <strong>{acc.name}</strong>
                        <span>{acc.authType === 'assumeRole' ? 'Assume Role' : 'Access Key'} · {acc.region}</span>
                      </span>
                      <span className="bedrock-account-actions">
                        <button className="bedrock-account-btn bedrock-account-btn-test"
                          onClick={() => isEmbed ? handleTestEmbedAccount(acc) : handleTestBedrockAccount(acc)}
                          disabled={testingId === acc.id}>{testingId === acc.id ? '…' : '테스트'}</button>
                        {(acc.inherited && !isProductAdmin && !isTenantAdmin)
                          ? <span style={{ fontSize: 11, color: 'var(--text-3)', padding: '0 6px' }}>상위에서 관리</span>
                          : <button className="bedrock-account-btn bedrock-account-btn-del"
                              onClick={() => isEmbed ? handleDeleteEmbedAccount(teamId, acc) : handleDeleteBedrockAccount(teamId, acc)}>삭제</button>
                        }
                      </span>
                    </label>
                  </div>
                ))}
              </div>
              <div className="provider-actions">
                <Button color="gray" appearance="outline"
                  onClick={() => isEmbed ? setSelectedEmbedTeamId(teamId) : setSelectedBedrockTeamId(teamId)}>
                  + 계정 추가
                </Button>
              </div>
            </>
          ) : (
            <div className="provider-actions">
              <Button color="primary" onClick={() => isEmbed ? setSelectedEmbedTeamId(teamId) : setSelectedBedrockTeamId(teamId)}>
                연결하기
              </Button>
            </div>
          )}
        </div>
      );
    }
    const status = (isEmbed ? d.embedStatuses : d.aiStatuses)?.[provider.id] || { connected: false };
    return (
      <div key={provider.id} className={`provider-card${status.connected ? ' connected' : ''}`}>
        <div className="provider-card-top">
          <div className="provider-logo"><img src={provider.logo} alt="" /></div>
          {status.connected ? <Badge color="success">연결됨</Badge> : <Badge color="gray">미연결</Badge>}
        </div>
        <div className="provider-name">{provider.name}</div>
        <div className="provider-actions">
          {status.connected
            ? <Button color="error" appearance="outline" onClick={() => handleDisconnect(teamId, provider.id, isEmbed)}>연결해제</Button>
            : <Button color="primary" onClick={() => setConfigProvider({ teamId, providerId: provider.id, isEmbed })}>연결하기</Button>
          }
        </div>
      </div>
    );
  };

  const renderProviderGrid = (teamId: string) => {
    const d = teamDataMap[teamId];
    if (!d?.loaded) return <div className="loading" style={{ padding: '30px 0', textAlign: 'center' }}>로딩 중...</div>;
    const isEmbed = activeTab === 'embedding';
    return (
      <div className="provider-grid">
        {(isEmbed ? EMBEDDING_PROVIDERS : AI_PROVIDERS).map(p => renderProviderCard(p, teamId, isEmbed, d))}
      </div>
    );
  };

  // ── 탭 ───────────────────────────────────────────────────────────────────
  const tabItems: TabItem<ProviderTab>[] = [
    { key: 'ai', label: '챗봇 AI 제공자' },
    { key: 'embedding', label: '임베딩 AI 제공자' },
  ];

  // 리스트: 현재 탭 기준으로 어카운트가 있는 팀만 표시
  const configuredTeams = allTeams.filter(t => {
    const d = teamDataMap[t.id];
    if (!d) return false;
    return isTeamConfiguredForTab(t.id, activeTab);
  });

  const renderTeamRow = (team: Team) => {
    const connected = getTeamConnectedProviders(team.id, activeTab) || [];
    return (
      <tr key={team.id} style={{ cursor: 'pointer' }} onClick={() => openSettingModal(team)}>
        <td className="left" style={{ fontWeight: 500 }}>{team.name}</td>
        <td>
          {connected.length === 0 ? (
            <span style={{ color: 'var(--text-3)', fontSize: 13 }}>—</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {connected.map(name => <span key={name} style={{ fontSize: 13, color: 'var(--text-2)' }}>{name}</span>)}
            </div>
          )}
        </td>
        <td><Badge color="success">연결됨</Badge></td>
        <td>
          <div className="user-actions">
            <button className="icon-action-btn" title="설정"
              onClick={e => { e.stopPropagation(); openSettingModal(team); }}>
              <EditIcon />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      {!embedded && <Topbar title="AI 제공자 연결" description="팀별로 AI 모델 제공자를 연결합니다" />}

      <div className={embedded ? '' : 'page-content'}>
        {embedded && <div className="admin-panel-head"><PanelTitle title="AI 제공자 연결" description="팀별로 AI 모델 제공자를 연결합니다." /></div>}

        <div className="tabs-row">
          <Tabs items={tabItems} active={activeTab} onChange={setActiveTab} />
          <div className="tabs-row-aside admin-panel-actions">
            {isProductAdmin && tenants.length > 0 && (
              <Select value={tenantFilter} onChange={e => { setTenantFilter(e.target.value); setCustomerFilter(''); }}
                style={{ minWidth: 120 }}>
                <option value="">전체 테넌트</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            )}
            {(isProductAdmin || isTenantAdmin) && (
              <Select value={customerFilter} onChange={e => setCustomerFilter(e.target.value)}
                style={{ minWidth: 120 }}>
                <option value="">전체 고객사</option>
                {customers.filter(c => !tenantFilter || c.tenantId === tenantFilter).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            )}
            <Button color="primary" onClick={() => setShowRegisterModal(true)}>+ 등록하기</Button>
          </div>
        </div>

        <div className={embedded ? 'admin-section' : 'page-content-inner'}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="left">팀</th>
                  <th>연결 현황</th>
                  <th>상태</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {configuredTeams.length === 0 ? (
                  <tr><td colSpan={4} className="table-empty">AI 제공자를 등록하세요.</td></tr>
                ) : (
                  configuredTeams.map(renderTeamRow)
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 등록하기 모달 */}
      <Modal isOpen={showRegisterModal}
        onClose={() => { setShowRegisterModal(false); if (!isTeamAdmin) { setRegTenantId(''); setRegCustomerId(''); setRegTeamId(''); setRegSelectedTeam(null); } }}
        title="팀 등록 / 설정" size="md"
        footerRight={<Button color="gray" appearance="outline" onClick={() => { setShowRegisterModal(false); if (!isTeamAdmin) { setRegTenantId(''); setRegCustomerId(''); setRegTeamId(''); setRegSelectedTeam(null); } }}>닫기</Button>}>
        {isTeamAdmin ? (
          // 팀 관리자: 선택 없이 바로 본인 팀 제공자 카드 표시
          regSelectedTeam ? (
            <>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
                {regSelectedTeam.name} 제공자 설정
              </div>
              {renderProviderGrid(regSelectedTeam.id)}
            </>
          ) : (
            <div className="loading" style={{ padding: '30px 0', textAlign: 'center' }}>로딩 중...</div>
          )
        ) : (
          <>
          <div className="form-grid" style={{ marginBottom: regSelectedTeam ? 16 : 0 }}>
            {isProductAdmin && tenants.length > 0 && (
              <div className="form-item">
                <div className="form-label">소속 테넌트</div>
                <Select value={regTenantId}
                  onChange={e => { setRegTenantId(e.target.value); setRegCustomers(customers.filter(c => c.tenantId === e.target.value)); setRegCustomerId(''); }}>
                  <option value="">테넌트 선택</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              </div>
            )}
            {(isProductAdmin || isTenantAdmin) && (
              <div className="form-item">
                <div className="form-label">소속 고객사 <span className="required">*</span></div>
                <Select value={regCustomerId} onChange={e => setRegCustomerId(e.target.value)}>
                  <option value="">고객사 선택</option>
                  {regCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
            )}
            <div className="form-item">
              <div className="form-label">소속 팀 <span className="required">*</span></div>
              <Select value={regTeamId} onChange={e => setRegTeamId(e.target.value)}
                disabled={(isProductAdmin || isTenantAdmin) && !regCustomerId}>
                <option value="">{(isProductAdmin || isTenantAdmin) && !regCustomerId ? '고객사를 먼저 선택' : '팀 선택'}</option>
                {regTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </div>
          </div>
          {regSelectedTeam && teamDataMap[regSelectedTeam.id] && (
            <>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
                {regSelectedTeam.name} 제공자 설정
              </div>
              {renderProviderGrid(regSelectedTeam.id)}
            </>
          )}
          </>
        )}
      </Modal>

      {/* 팀 설정 모달 */}
      <Modal isOpen={!!settingTeam} onClose={() => setSettingTeam(null)}
        title={settingTeam ? `${settingTeam.name} — ${activeTab === 'ai' ? 'AI 제공자' : '임베딩 AI 제공자'} 설정` : ''}
        size="md"
        footerRight={<Button color="gray" appearance="outline" onClick={() => setSettingTeam(null)}>닫기</Button>}>
        {settingTeam && renderProviderGrid(settingTeam.id)}
      </Modal>

      {/* API Key 입력 모달 */}
      <Modal isOpen={!!configProvider}
        onClose={() => { setConfigProvider(null); setApiKeyInput(''); setApiKeyTested(false); }}
        title={configProvider ? (() => {
          const all = [...AI_PROVIDERS, ...EMBEDDING_PROVIDERS];
          return (all.find(p => p.id === configProvider.providerId)?.name ?? configProvider.providerId) + ' API Key 연결';
        })() : ''}
        size="md"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => { setConfigProvider(null); setApiKeyInput(''); setApiKeyTested(false); }}>닫기</Button>
            <Button color="primary" onClick={handleSaveApiKey} disabled={!apiKeyTested}>저장</Button>
          </>
        }>
        {configProvider && (() => {
          const providerMeta: Record<string, { desc: string; linkLabel: string; url: string; note?: string }> = {
            openai:    { desc: 'OpenAI 플랫폼에서 프로젝트 API Key를 발급받으세요.',          linkLabel: 'OpenAI Platform → API Keys',             url: 'https://platform.openai.com/api-keys',
                         note: '연결 테스트는 API Key 유효성만 확인합니다. 실제 모델 사용은 OpenAI 계정에 크레딧이 있어야 합니다.' },
            anthropic: { desc: 'Anthropic Console에서 API Key를 생성할 수 있습니다.',          linkLabel: 'Anthropic Console → API Keys',            url: 'https://console.anthropic.com/settings/keys',
                         note: 'Anthropic은 연결 테스트 시 실제 API를 호출합니다. 계정에 크레딧이 있어야 테스트가 가능합니다.' },
            google:    { desc: 'Google AI Studio에서 Gemini용 API Key를 무료로 발급받으세요.', linkLabel: 'Google AI Studio → Get API Key',           url: 'https://aistudio.google.com/apikey' },
          };
          const meta = providerMeta[configProvider.providerId];
          return (
            <div className="apikey-modal-body">
              {/* 1단계: 발급 안내 */}
              {meta && (
                <div>
                  <div className="apikey-step-title">1단계: API Key 발급</div>
                  <div className="apikey-info-box">
                    <p className="apikey-info-desc">{meta.desc}</p>
                    <a href={meta.url} target="_blank" rel="noopener noreferrer" className="apikey-info-link">
                      🔗 {meta.linkLabel}
                      <span>↗</span>
                    </a>
                    {meta.note && (
                      <div className="apikey-note">
                        <span>⚠️</span>
                        <span>{meta.note}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 2단계: 입력 + 테스트 */}
              <div>
                <div className="apikey-step-title">{meta ? '2단계: API Key 입력 및 테스트' : 'API Key 입력'}</div>
                <div className="bedrock-ext-row">
                  <input className="input-control" type="password" placeholder="API Key를 입력하세요"
                    value={apiKeyInput}
                    onChange={e => { setApiKeyInput(e.target.value); setApiKeyTested(false); }}
                    autoFocus />
                  <Button color="primary" appearance="outline" onClick={handleTestApiKey}
                    disabled={!apiKeyInput.trim() || apiKeyTesting}>
                    {apiKeyTesting ? '테스트 중...' : '연결 테스트'}
                  </Button>
                </div>
                {apiKeyTested && (
                  <div className="apikey-test-success">
                    <span>✓</span><span>연결이 확인되었습니다. 저장 버튼을 눌러 완료하세요.</span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      {selectedBedrockTeamId && (
        <BedrockAccountModal isOpen={!!selectedBedrockTeamId} onClose={() => setSelectedBedrockTeamId(null)}
          onSaved={() => { const tid = selectedBedrockTeamId; refreshTeam(tid).then(() => setSelectedBedrockTeamId(null)); }}
          defaultScopeOverride={`team:${selectedBedrockTeamId}`} />
      )}
      {selectedEmbedTeamId && (
        <EmbeddingAccountModal isOpen={!!selectedEmbedTeamId} onClose={() => setSelectedEmbedTeamId(null)}
          onSaved={() => { const tid = selectedEmbedTeamId; refreshTeam(tid).then(() => setSelectedEmbedTeamId(null)); }}
          defaultScopeOverride={`team:${selectedEmbedTeamId}`} />
      )}

      {/* 삭제 확인 모달 */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="계정 삭제"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="dialog-body--compact"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setDeleteConfirm(null)}>취소</Button>
            <Button color="error" onClick={executeDelete}>삭제</Button>
          </>
        }
      >
        <p style={{ fontSize: 14, color: 'var(--text-2)' }}>
          <strong>"{deleteConfirm?.acc?.name}"</strong> 계정을 삭제하시겠습니까?
        </p>
      </Modal>
    </>
  );
}

export default ProviderConnectionPage;
